import React from 'react';
import { motion } from 'framer-motion';
import { formatDecimal } from '../utils/formatUtils';

const AlakartFinalSummary = ({ weeklyPlan, consolidatedIngredients, onClose, isDarkMode, onApprove, isApproving }) => {
    const categoryIcons = {
        SOUP: 'soup_kitchen',
        MAIN: 'restaurant_menu',
        SALAD: 'eco',
        DESSERT: 'icecream'
    };

    const categoryLabels = {
        SOUP: 'Çorba',
        MAIN: 'Ana Yemek',
        SALAD: 'Salata',
        DESSERT: 'Tatlı'
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-7xl bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-primary to-red-500 p-6 md:p-8 text-white relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                    <div className="flex items-center gap-3 mb-2">
                        <span className="material-symbols-outlined text-4xl">calendar_month</span>
                        <h2 className="text-3xl md:text-4xl font-black">Başlangıç Plan Özeti</h2>
                    </div>
                    <p className="text-white/80 text-sm md:text-base">7 günlük başlangıç hazırlığınız ve eksik malzemeleriniz</p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Column: Weekly Menu */}
                        <div>
                            <div className="flex items-center gap-2 mb-6">
                                <span className="material-symbols-outlined text-2xl text-primary">restaurant</span>
                                <h3 className="text-2xl font-black text-slate-900 dark:text-white">7 Günlük Başlangıç Planınız</h3>
                            </div>
                            <div className="space-y-4">
                                {weeklyPlan.map((day, index) => (
                                    <div
                                        key={index}
                                        className="bg-slate-50 dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-700"
                                    >
                                        <div className="flex items-center gap-2 mb-3">
                                            <span className="material-symbols-outlined text-primary text-lg">calendar_today</span>
                                            <h4 className="font-black text-slate-900 dark:text-white">{day.date}</h4>
                                        </div>
                                        <div className="space-y-2">
                                            {day.meals.map((meal, mealIndex) => (
                                                <div key={mealIndex} className="flex items-start gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-red-500 flex items-center justify-center shrink-0">
                                                        <span className="material-symbols-outlined text-white text-sm">
                                                            {categoryIcons[meal.category]}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                                            {categoryLabels[meal.category]}
                                                        </div>
                                                        <div className="text-sm font-bold text-slate-900 dark:text-white">
                                                            {meal.name}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Right Column: Shopping List */}
                        <div>
                            <div className="flex items-center gap-2 mb-6">
                                <span className="material-symbols-outlined text-2xl text-green-500">shopping_cart</span>
                                <h3 className="text-2xl font-black text-slate-900 dark:text-white">Alışveriş Listesi</h3>
                            </div>
                            {consolidatedIngredients.length === 0 ? (
                                <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-8 text-center border border-green-200 dark:border-green-800">
                                    <span className="material-symbols-outlined text-6xl text-green-500 mb-3">check_circle</span>
                                    <p className="text-green-700 dark:text-green-300 font-bold">
                                        Tüm malzemeler stokta!
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-slate-50 dark:bg-slate-900 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
                                    <div className="space-y-3">
                                        {consolidatedIngredients.map((item, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700 last:border-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                                    <span className="font-bold text-slate-900 dark:text-white">
                                                        {item.name}
                                                    </span>
                                                </div>
                                                <span className="text-sm font-black text-primary">
                                                    {formatDecimal(item.quantity)} {item.unit}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="font-bold text-slate-600 dark:text-slate-400">Toplam Kalem</span>
                                            <span className="font-black text-primary text-lg">{consolidatedIngredients.length}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-slate-200 dark:border-slate-700 p-6 bg-slate-50 dark:bg-slate-900">
                    <button
                        onClick={onApprove}
                        disabled={isApproving}
                        className={`w-full text-white font-black py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2 ${isApproving
                            ? 'bg-slate-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-primary to-red-500'
                            }`}
                    >
                        {isApproving ? (
                            <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span className="material-symbols-outlined">check_circle</span>
                        )}
                        Planı Onayla ve Eksikleri Listeye Ekle
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default AlakartFinalSummary;
